import { Message, ResponseCallback, MessageEndpoint, SyncResponse, SessionInitMessage, SessionCloseMessage } from '@augloop/session-protocol';
import { Operation as LogOperation, CorrelationVector } from '@augloop/logging/dist/src/client/logging';
import { SchemaObject } from '@augloop/types-core';
import { IMessageQueue } from './message-queue';
import { IWebSocketWorkerManager } from './websocket-worker-manager';
import { SessionStats } from './isession-manager';

export enum SessionState {
    Initing,
    Running,
    Disconnected,
    Closed
}

export type OnStateEnterParameters = {
    isSessionReseedingStarted?: boolean;
}

export interface ISessionState {
    readonly stateName: SessionState;
    readonly possibleNextStates: SessionState[];
    sendMessage(message: Message, onResponse?: ResponseCallback, attemptNumber?: number): void;
    sendBytes(buffer: Uint8Array): void;
    canSendMessage(): boolean;
    onEnter(params?: OnStateEnterParameters): void;
    onConnectionClose(): void;
}

export interface ISessionStateContext {
    readonly cvParent: CorrelationVector;
    readonly messageEndpoint: MessageEndpoint;
    readonly webSocketWorkerManager: IWebSocketWorkerManager;
    readonly messageQueue: IMessageQueue;
    readonly stats: SessionStats;
    readonly logOp: LogOperation;
    readonly resetNextSyncSequenceId: () => void;
    readonly customInitPromise?: () => Promise<void>;
    setState(newState: SessionState, reason?: string): void;
}

export const sendMessageNow = (context: ISessionStateContext, message: Message, onResponse?: ResponseCallback, attemptNumber?: number): void => {
    if (!message) {
        return;
    }

    if (!message.cv) {
        message.cv = context.cvParent.newChild().toString();
    }
    context.messageEndpoint.sendMessage(message, (err, resp) => {
        // Record when was the last sync message response received
        if (!err && SchemaObject.getTypeNameFor(resp) === SyncResponse.getTypeName()) {
            context.stats.lastSyncMessage = Date.now();
        }

        if (onResponse) {
            onResponse(err, resp);
        }
    }, undefined, attemptNumber);
};

export const queueMessage = (context: ISessionStateContext, message: Message, onResponse?: ResponseCallback, attemptNumber?: number): void => {
    if (!message) {
        return;
    }

    context.messageQueue.push(
        message,
        onResponse,
        attemptNumber);
    context.webSocketWorkerManager.init(undefined/*extensionConfigs*/, context.customInitPromise);
};

export const queueBytes = (context: ISessionStateContext, buffer: Uint8Array): void => {
    if (!buffer) {
        return;
    }

    context.messageQueue.push(buffer);
    context.webSocketWorkerManager.init(undefined/*extensionConfigs*/, context.customInitPromise);
};

export abstract class BaseState {
    public readonly stateName: SessionState;
    protected readonly context: ISessionStateContext;

    public constructor(context: ISessionStateContext) {
        this.context = context;
    }

    public onEnter(_params?: OnStateEnterParameters): void {
    }

    public sendMessage(_message: Message, _onResponse: ResponseCallback, _attemptNumber?: number): void {
    }

    public sendBytes(_buffer: Uint8Array): void {
    }

    public canSendMessage(): boolean {
        return false;
    }

    public onConnectionClose(): void {
    }
}

export class InitingState extends BaseState implements ISessionState {
    public readonly stateName: SessionState = SessionState.Initing;
    public readonly possibleNextStates: SessionState[] = [SessionState.Running, SessionState.Disconnected, SessionState.Closed];

    public sendMessage(message: Message, onResponse: ResponseCallback, attemptNumber?: number): void {
        if (SchemaObject.matchesTypesFor(message, [SessionInitMessage.getTypeName()])) {
            sendMessageNow(this.context, message, onResponse, attemptNumber);
        } else {
            queueMessage(this.context, message, onResponse);
        }
    }

    public sendBytes(buffer: Uint8Array): void {
        queueBytes(this.context, buffer);
    }

    public onConnectionClose(): void {
        this.context.setState(SessionState.Disconnected);
    }
}

export class RunningState extends BaseState implements ISessionState {
    public readonly stateName: SessionState = SessionState.Running;
    public readonly possibleNextStates: SessionState[] = [SessionState.Disconnected, SessionState.Closed];

    public onEnter(params?: OnStateEnterParameters): void {
        if (params && params.isSessionReseedingStarted) {
            this.context.resetNextSyncSequenceId();
        }
        this.context.messageQueue.sendOnSessionInitialized(params && params.isSessionReseedingStarted);
    }

    public sendMessage(message: Message, onResponse: ResponseCallback, attemptNumber?: number): void {
        sendMessageNow(this.context, message, onResponse, attemptNumber);
    }

    public sendBytes(buffer: Uint8Array): void {
        if (buffer) {
            this.context.webSocketWorkerManager.egressBytes(buffer);
        }
    }

    public canSendMessage(): boolean {
        return true;
    }

    public onConnectionClose(): void {
        this.context.setState(SessionState.Disconnected);
    }
}

export class DisconnectedState extends BaseState implements ISessionState {
    public readonly stateName: SessionState = SessionState.Disconnected;
    public readonly possibleNextStates: SessionState[] = [SessionState.Initing, SessionState.Closed];

    public onEnter(_params?: OnStateEnterParameters): void {
        this.context.messageEndpoint.cancelPendingResponseCallbacks();
    }

    public sendMessage(message: Message, onResponse: ResponseCallback, attemptNumber?: number): void {
        if (SchemaObject.matchesTypesFor(message, [SessionInitMessage.getTypeName()])) {
            this.context.setState(SessionState.Initing);
            sendMessageNow(this.context, message, onResponse, attemptNumber);
        } else if (SchemaObject.matchesTypesFor(message, [SessionCloseMessage.getTypeName()])) {
            this.context.setState(SessionState.Closed);
        } else {
            queueMessage(this.context, message, onResponse, attemptNumber);
        }
    }

    public sendBytes(buffer: Uint8Array): void {
        queueBytes(this.context, buffer);
    }
}

export class ClosedState extends BaseState implements ISessionState {
    public readonly state: SessionState = SessionState.Closed;
    public readonly possibleNextStates: SessionState[] = [];
}
